import ProductSectionSearch from "@/app/components/ui/Reusable/ProductSectionSearch";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useClinicWarehouseDetailsByCategoryIdAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useClinicWarehouseDetailsByCategoryIdAPI";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useCouponSubCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

export interface IInventorySnapshotProps {
  cityId: string;
  clinics: string[];
  variant_category_name?: string[];
  productId: string;
  productName: string;
  vmsSubcategory: string[];
}

const InventoryMovementFilters = ({ setProductFilters }) => {
  const { clinicWarehouseDetails, cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const clinicWarehouseOptions = useSelectMenuReducer(clinicWarehouseDetails, "clinic_name", "clinic_id");
  const [_selectedClinic, setSelectedClinic] = useState<any>(null);
  const fetchClinicWarehouseDetails = useClinicWarehouseDetailsByCategoryIdAPI();
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");
  const { execute: fetchCategory, serviceCategory } = useCouponCategoryAPI();
  const { execute: fetchSubCategory, serviceSubCategory } = useCouponSubCategoryAPI();
  const couponCategoryItem = useSelectMenuReducer(serviceCategory, "category_name", "id");

  const [initialFetch, setInitialFetch] = useState(false);
  const [initialRender, setInitialRender] = useState(false);

  const couponSubCategoryItem = useSelectMenuReducer(serviceSubCategory, "subcategory_name", "subcategory_id");
  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const formHook = useForm<IInventorySnapshotProps>({
    mode: "onChange",
    defaultValues: {
      cityId: mappedCityId,
      clinics: [mappedClinicId],
    },
  });

  useEffect(() => {
    const formData = formHook.getValues();
    if (formData.variant_category_name?.length > 0) {
      fetchSubCategory(formData.variant_category_name);
      fetchClinicWarehouseDetails(formData.variant_category_name?.join(), formData?.cityId);
    }
  }, [formHook.watch("variant_category_name")]);

  useEffect(() => {
    fetchCities();
    fetchCategory();
  }, []);
  useEffect(() => {
    if (cities) {
      setInitialFetch(false);
    }
  }, [cities]);

  useEffect(() => {
    const clinic_id = localStorage.getItem("clinic_snp");
    setTimeout(() => {
      formHook.setValue("clinics", [clinic_id]);
    }, 800);
  }, []);

  useEffect(() => {
    const data = formHook.getValues();
    if (
      data?.cityId &&
      data?.variant_category_name?.length > 0 &&
      data?.clinics?.length > 0 &&
      data.vmsSubcategory?.length > 0 &&
      !initialRender
    ) {
      const productId = formHook.getValues("productId");
      const filters = {
        clinic_ids: formHook.getValues("clinics"),
        category_ids: formHook.getValues("variant_category_name"),
        sub_category_ids: formHook.getValues("vmsSubcategory"),
      } as {
        clinic_ids: string[];
        category_ids: string[];
        vetic_variant_id?: string;
        sub_category_ids: string[];
      };
      localStorage.setItem("clinic_snp", formHook.getValues("clinics").join());
      if (productId) {
        filters.vetic_variant_id = productId;
      }
      setProductFilters(filters);
      setInitialRender(true);
    }
  }, [
    formHook.watch("cityId"),
    formHook.watch("variant_category_name"),
    formHook.watch("clinics"),
    formHook.watch("vmsSubcategory"),
  ]);

  useEffect(() => {
    if (clinicWarehouseOptions?.length > 0 && !initialFetch && mappedClinicId && serviceCategory?.length > 0) {
      formHook.setValue("clinics", [mappedClinicId]);
      //
      setInitialFetch(true);
    }
  }, [clinicWarehouseOptions?.length > 0, mappedClinicId, serviceCategory?.length > 0]);

  const Submit = () => {
    if (!formHook.getValues("clinics")) {
      toast.error("Clinic is Required");
      return;
    }

    const productId = formHook.getValues("productId");
    const filters = {
      clinic_ids: formHook.getValues("clinics"),
      category_ids: formHook.getValues("variant_category_name"),
      sub_category_ids: formHook.getValues("vmsSubcategory"),
    } as {
      clinic_ids: string[];
      category_ids: string[];
      vetic_variant_id?: string;
      sub_category_ids: string[];
    };

    if (productId) {
      filters.vetic_variant_id = productId;
    }
    setProductFilters(filters);
  };

  return (
    <div>
      <form className="flex justify-between items-center mt-1 2xl:mt-2" onSubmit={formHook.handleSubmit(Submit)}>
        <div className="flex items-center gap-5 w-full">
          <div className="w-52">
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: null }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinics", []);
                  formHook.setValue("variant_category_name", []);
                  formHook.setValue("cityId", item.id);
                }
              }}
              fieldError={formHook.formState.errors.cityId}
              register={formHook.register("cityId", { required: true })}
              selectItems={cityOrgItem}
              placeholder="Select City"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={cityOrgItem?.filter((oc) => oc.id === formHook.watch("cityId"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-56">
            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll
              errorMessages={[{ type: "required", message: "Category is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinics", []);
                  const categoriesId = item.map((i) => i.id);
                  formHook.setValue("variant_category_name", categoriesId);
                  // if (categoriesId?.length > 0 && cityId) fetchClinicWarehouseDetails(categoriesId, cityId);
                }
              }}
              fieldError={formHook.formState.errors.variant_category_name}
              register={formHook.register("variant_category_name", { required: true })}
              selectItems={couponCategoryItem}
              placeholder="Select Category"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              withDropDownSearch
              defaultSelected={couponCategoryItem?.filter((c) =>
                formHook.watch("variant_category_name")?.includes(c.id),
              )}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-60">
            <SearchableMultiSelectMenu
              withSelectAll
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const clinicsId = item.map((i) => i.id);
                  formHook.setValue("clinics", clinicsId);
                  setSelectedClinic(clinicsId);
                }
              }}
              disabled={!formHook.getValues("cityId") || formHook.getValues("variant_category_name")?.length == 0}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics")}
              selectItems={clinicWarehouseOptions}
              placeholder="Select Clinics"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicWarehouseOptions?.filter((oc) => formHook.watch("clinics")?.includes(oc.id))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-56">
            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const categoriesId = item.map((i) => i.id);
                  formHook.setValue("vmsSubcategory", categoriesId);
                }
              }}
              fieldError={formHook.formState.errors.vmsSubcategory}
              register={formHook.register("vmsSubcategory", { required: false })}
              selectItems={couponSubCategoryItem}
              placeholder="Select Sub-Category"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={couponSubCategoryItem?.filter((c) => formHook.watch("vmsSubcategory")?.includes(c.id))}
              listBoxClassName="w-36"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="flex items-center gap-2 pb-3 w-80">
            <ProductSectionSearch
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("productId", item.id);
                  formHook.setValue("productName", item.name);
                } else {
                  formHook.setValue("productId", null);
                  formHook.setValue("productName", null);
                }
              }}
              containerClassName="mt-2 w-full shadow-none"
              itemActiveClassName="bg-orange-500 text-white"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              searchType="Lab"
            />
          </div>
          <div>
            <input
              type="submit"
              className="bg-black px-6 py-2 border-transparent rounded-md text-sm text-white cursor-pointer"
              value={"Search"}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default InventoryMovementFilters;
